import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { scrollToId } from "./scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxob21lJTIwcmVtb2RlbGluZyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODc2MTg5NDF8MA&ixlib=rb-4.1.0&q=85";

const ease = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ categories }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [service, setService] = useState("");
  const [zip, setZip] = useState("");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onSearch = (e) => {
    e.preventDefault();
    toast.info("Pro search launches with the full platform. Browse our services below.");
    scrollToId("services");
  };

  return (
    <section id="hero" ref={ref} className="relative bg-navy grain overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 lg:pt-44 lg:pb-32 grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease }}
            className="flex items-center gap-3 uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="w-2 h-2 bg-flame animate-pulse" />
            Now launching — Huntsville &amp; North Alabama
          </motion.p>

          <h1 data-testid="hero-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02]">
            <RevealLine delay={0.2}>Find Trusted</RevealLine>
            <RevealLine delay={0.35}>Local Professionals</RevealLine>
            <RevealLine delay={0.5}>
              <span className="text-flame">Near You.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease }}
            className="text-white/60 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
          >
            Find reliable local professionals for home and property services.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease }}
          >
            <button
              data-testid="hero-request-estimate-btn"
              onClick={() => navigate("/request/new")}
              className="mt-8 inline-flex items-center gap-3 bg-flame hover:bg-flame-dark text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-4 transition-transform hover:scale-[1.03]"
            >
              Request a Free Estimate <ArrowRight size={16} />
            </button>
          </motion.div>

          <motion.form
            onSubmit={onSearch}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease }}
            className="mt-8 flex flex-col sm:flex-row bg-white p-2 gap-2 max-w-2xl"
            data-testid="hero-search-form"
          >
            <div className="flex items-center flex-1 px-3">
              <Search size={18} className="text-flame shrink-0" />
              <select
                data-testid="hero-service-select"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="w-full h-12 bg-transparent text-navy text-sm font-medium focus:outline-none px-2 cursor-pointer"
              >
                <option value="">What service do you need?</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center px-3 sm:border-l border-border">
              <MapPin size={18} className="text-flame shrink-0" />
              <input
                data-testid="hero-zip-input"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="ZIP Code"
                inputMode="numeric"
                className="w-full sm:w-28 h-12 bg-transparent text-navy text-sm font-medium focus:outline-none px-2 focus:ring-2 focus:ring-flame"
              />
            </div>
            <button
              data-testid="hero-search-btn"
              type="submit"
              className="bg-flame hover:bg-flame-dark text-white font-heading font-bold uppercase tracking-wider text-sm px-8 h-12 sm:h-auto transition-transform hover:scale-[1.02]"
            >
              Find Pros
            </button>
          </motion.form>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-white/40 text-xs mt-4 tracking-wide"
          >
            Serving Huntsville and surrounding communities in Alabama, with expansion into Tennessee.
          </motion.p>
        </div>

        <div className="md:col-span-5 relative hidden md:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.6, ease }}
            className="relative"
          >
            <div className="absolute -top-5 -right-5 w-full h-full border-2 border-flame" />
            <div className="relative overflow-hidden aspect-[4/5]">
              <motion.img
                src={HERO_IMG}
                alt="Modern home project in North Alabama"
                style={{ y: imgY }}
                className="w-full h-[115%] object-cover"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white px-6 py-4 shadow-xl">
              <p className="font-heading font-black text-navy text-lg leading-tight">Alabama &amp; Tennessee</p>
              <p className="text-xs text-slate-500 tracking-wide mt-1">Serving local communities first</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
